import { useState } from "react";
import { Helmet } from "react-helmet";
import { Formik } from "formik";
import * as Yup from "yup";
import { Alert, Button, Card, Form } from "react-bootstrap";
import Swal from "sweetalert2";

import "./style.scss";
import { ClinicApi } from "../../api";
import { CustomBreadCrumb, Loading } from "../../components";
import { useAppNavigate } from "../../Router/hook";
import { useAppSelector } from "../../redux-config/hooks";
import { selectUser } from "../../redux-config/entities/user";

type ClinicCreateFormValues = {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    condition: string;
    therapy: string;
};

const conditionOptions = ["CM Risk", "T2D", "Prediabetes"];
const therapyOptions = ["GLP-1", "Metformin", "Lifestyle", "insulin", "oral medication"];

const clinicCreateValidationSchema = Yup.object({
    firstName: Yup.string().trim().required("First name is required."),
    lastName: Yup.string().trim().required("Last name is required."),
    email: Yup.string().trim().email("Enter a valid email address."),
    phone: Yup.string().trim(),
    condition: Yup.string().trim().oneOf(["", ...conditionOptions]),
    therapy: Yup.string().trim().oneOf(["", ...therapyOptions]),
}).test(
    "email-or-phone-required",
    "Email or phone is required.",
    function (values) {
        if (!values) {
            return this.createError({
                path: "email",
                message: "Email or phone is required.",
            });
        }

        if (values.email?.trim() || values.phone?.trim()) {
            return true;
        }

        return this.createError({
            path: "email",
            message: "Email or phone is required.",
        });
    },
);

const mapApiErrorsToForm = (errors: Record<string, string[]>) => {
    const formErrors: Record<string, string> = {};

    Object.entries(errors || {}).forEach(([key, value]) => {
        const message = value?.[0];

        if (!message) {
            return;
        }

        if (key.includes("patient.first_name")) {
            formErrors.firstName = message;
        } else if (key.includes("patient.last_name")) {
            formErrors.lastName = message;
        } else if (key.includes("patient.email")) {
            formErrors.email = message;
        } else if (key.includes("patient.phone")) {
            formErrors.phone = message;
        } else if (key.includes("patient_profile.condition")) {
            formErrors.condition = message;
        } else if (key.includes("patient_profile.therapy")) {
            formErrors.therapy = message;
        }
    });

    return formErrors;
};

export const CreateClinicPatient = () => {
    const user = useAppSelector(selectUser);
    const navigate = useAppNavigate();
    const [loading, setLoading] = useState(false);

    const crumbs = [
        { name: "", href: "" },
        { name: "Clinic", href: "/clinic/patients" },
        { name: "Create Patient", href: "#" },
    ];

    return (
        <div className="my-4 clinic-page">
            <Helmet>
                <title>D2Type | Create Clinic Patient</title>
            </Helmet>

            {loading && <Loading />}

            <CustomBreadCrumb breadCrumbList={crumbs} />

            <div className="clinic-form-card clinic-shell">
                <div className="clinic-hero mb-4">
                    <div className="text-uppercase text-muted small fw-semibold mb-2">
                        New referral
                    </div>
                    <h2 className="mb-2">Create patient referral</h2>
                    <p className="text-muted mb-0">
                        Keep this form short. Only patient identity and referral
                        details are needed at this stage.
                    </p>
                </div>

                <Card className="clinic-surface border-0">
                    <Card.Body className="clinic-panel">

                        <Formik<ClinicCreateFormValues>
                            initialValues={{
                                firstName: "",
                                lastName: "",
                                email: "",
                                phone: "",
                                condition: "",
                                therapy: "",
                            }}
                            validationSchema={clinicCreateValidationSchema}
                            onSubmit={async (
                                values,
                                { setErrors, setStatus },
                            ) => {
                                try {
                                    setLoading(true);
                                    setStatus(undefined);

                                    const response =
                                        await ClinicApi.CreateClinicPatient(user, {
                                            patient: {
                                                first_name: values.firstName.trim(),
                                                last_name: values.lastName.trim(),
                                                email: values.email.trim() || undefined,
                                                phone: values.phone.trim() || undefined,
                                            },
                                            patient_profile: {
                                                condition:
                                                    values.condition.trim() || undefined,
                                                therapy:
                                                    values.therapy.trim() || undefined,
                                            },
                                        });

                                    navigate(
                                        `/clinic/patients/${response.patient.id}`,
                                        {
                                            state: {
                                                successMessage:
                                                    "Install links were sent to the patient.",
                                            },
                                        },
                                    );
                                } catch (err: any) {
                                    const apiErrors = err?.data?.errors;

                                    if (apiErrors) {
                                        setErrors(mapApiErrorsToForm(apiErrors));
                                        setStatus(
                                            err?.data?.message ||
                                                "Please review the highlighted fields.",
                                        );
                                    } else {
                                        Swal.fire({
                                            icon: "error",
                                            text:
                                                err?.data?.message ||
                                                err?.message ||
                                                "Unable to create clinic patient.",
                                        });
                                    }
                                } finally {
                                    setLoading(false);
                                }
                            }}
                        >
                            {({
                                handleSubmit,
                                handleChange,
                                handleBlur,
                                values,
                                touched,
                                errors,
                                isSubmitting,
                                status,
                            }) => (
                                <Form onSubmit={handleSubmit}>
                                    {status && (
                                        <Alert variant="danger">{status}</Alert>
                                    )}

                                    <div className="clinic-detail-stack">
                                        <Card className="border-0 bg-light-subtle">
                                            <Card.Body>
                                                <h3 className="h6 mb-3">
                                                    Patient identity
                                                </h3>
                                                <div className="row g-3">
                                                    <div className="col-12 col-md-6">
                                                        <Form.Label>First name</Form.Label>
                                                        <Form.Control
                                                            name="firstName"
                                                            value={values.firstName}
                                                            onChange={handleChange}
                                                            onBlur={handleBlur}
                                                            isInvalid={
                                                                touched.firstName &&
                                                                !!errors.firstName
                                                            }
                                                            placeholder="Patient first name"
                                                        />
                                                        <Form.Control.Feedback type="invalid">
                                                            {errors.firstName}
                                                        </Form.Control.Feedback>
                                                    </div>
                                                    <div className="col-12 col-md-6">
                                                        <Form.Label>Last name</Form.Label>
                                                        <Form.Control
                                                            name="lastName"
                                                            value={values.lastName}
                                                            onChange={handleChange}
                                                            onBlur={handleBlur}
                                                            isInvalid={
                                                                touched.lastName &&
                                                                !!errors.lastName
                                                            }
                                                            placeholder="Patient last name"
                                                        />
                                                        <Form.Control.Feedback type="invalid">
                                                            {errors.lastName}
                                                        </Form.Control.Feedback>
                                                    </div>
                                                </div>
                                            </Card.Body>
                                        </Card>

                                        <Card className="border-0 bg-light-subtle">
                                            <Card.Body>
                                                <h3 className="h6 mb-3">
                                                    Contact and referral
                                                </h3>
                                                <div className="row g-3">
                                                    <div className="col-12 col-md-6">
                                                        <Form.Label>Email</Form.Label>
                                                        <Form.Control
                                                            name="email"
                                                            value={values.email}
                                                            onChange={handleChange}
                                                            onBlur={handleBlur}
                                                            isInvalid={
                                                                touched.email &&
                                                                !!errors.email
                                                            }
                                                            placeholder="jane@example.com"
                                                        />
                                                        <Form.Control.Feedback type="invalid">
                                                            {errors.email}
                                                        </Form.Control.Feedback>
                                                    </div>
                                                    <div className="col-12 col-md-6">
                                                        <Form.Label>Phone</Form.Label>
                                                        <Form.Control
                                                            name="phone"
                                                            value={values.phone}
                                                            onChange={handleChange}
                                                            onBlur={handleBlur}
                                                            isInvalid={
                                                                touched.phone &&
                                                                !!errors.phone
                                                            }
                                                            placeholder="+14155552671"
                                                        />
                                                        <Form.Control.Feedback type="invalid">
                                                            {errors.phone}
                                                        </Form.Control.Feedback>
                                                    </div>
                                                    <div className="col-12 col-md-6">
                                                        <Form.Label>Condition</Form.Label>
                                                        <Form.Select
                                                            name="condition"
                                                            value={values.condition}
                                                            onChange={handleChange}
                                                            onBlur={handleBlur}
                                                            isInvalid={
                                                                touched.condition &&
                                                                !!errors.condition
                                                            }
                                                        >
                                                            <option value="">Select condition</option>
                                                            {conditionOptions.map((option) => (
                                                                <option key={option} value={option}>
                                                                    {option}
                                                                </option>
                                                            ))}
                                                        </Form.Select>
                                                        <Form.Control.Feedback type="invalid">
                                                            {errors.condition}
                                                        </Form.Control.Feedback>
                                                    </div>
                                                    <div className="col-12 col-md-6">
                                                        <Form.Label>Therapy</Form.Label>
                                                        <Form.Select
                                                            name="therapy"
                                                            value={values.therapy}
                                                            onChange={handleChange}
                                                            onBlur={handleBlur}
                                                            isInvalid={
                                                                touched.therapy &&
                                                                !!errors.therapy
                                                            }
                                                        >
                                                            <option value="">Select therapy</option>
                                                            {therapyOptions.map((option) => (
                                                                <option key={option} value={option}>
                                                                    {option}
                                                                </option>
                                                            ))}
                                                        </Form.Select>
                                                        <Form.Control.Feedback type="invalid">
                                                            {errors.therapy}
                                                        </Form.Control.Feedback>
                                                    </div>
                                                </div>
                                            </Card.Body>
                                        </Card>
                                    </div>

                                    <div className="d-flex flex-column flex-sm-row justify-content-end gap-2 mt-4">
                                        <Button
                                            variant="outline-secondary"
                                            onClick={() => navigate("/clinic/patients")}
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            type="submit"
                                            variant="success"
                                            disabled={loading || isSubmitting}
                                        >
                                            Create referral
                                        </Button>
                                    </div>
                                </Form>
                            )}
                        </Formik>
                    </Card.Body>
                </Card>
            </div>
        </div>
    );
};
